import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useUserPermissions from '../../../hooks/useUserPermissions';
import api from '../../../utils/api';
import { formatCurrency, formatPercent, formatDate } from '../../../utils/formatters';
import StatCard from '../../common/StatCard/StatCard';
import UserSelector from '../../common/UserSelector';
import './FuturesDashboard.css';

export default function FuturesDashboard() {
  const navigate = useNavigate();
  const { isAdmin, currentUserId } = useUserPermissions();
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [positions, setPositions] = useState([]);
  const [stats, setStats] = useState(null);
  const [targetUserId, setTargetUserId] = useState(currentUserId);
  
  useEffect(() => {
    if (!targetUserId) return;
    fetchData();
    const interval = setInterval(fetchData, 10000);
    return () => clearInterval(interval);
  }, [targetUserId]);
  
  const fetchData = async () => {
    try {
      const configData = await api.getFuturesConfig(targetUserId);
      setConfig(configData);
      
      try {
        const dashboardData = await api.getFuturesDashboard(targetUserId);
        setPositions(dashboardData.positions || []);
        setStats(dashboardData.performance || {
          totalTrades: 0,
          winRate: 0,
          totalPnl: 0,
          unrealizedPnl: 0
        });
      } catch (err) {
        console.warn('Dashboard endpoint not available:', err);
        setPositions([]);
        setStats({
          totalTrades: 0,
          winRate: 0,
          totalPnl: 0,
          unrealizedPnl: 0
        });
      }
    } catch (err) {
      console.error('Error fetching futures data:', err);
    } finally {
      setLoading(false);
    }
  };
  
  if (loading) {
    return (
      <div className="futures-dashboard-pro">
        <div className="loading-center">
          <div className="spinner-large"></div>
          <p>Loading Futures Bot...</p>
        </div>
      </div>
    );
  }
  
  if (!config) {
    return (
      <div className="futures-dashboard-pro">
        <div className="empty-center">
          <h2>⚡ Futures Bot Not Configured</h2>
          <p>Please configure the Futures bot first</p>
          {isAdmin && (
            <button className="btn btn-primary" onClick={() => navigate('/futures/config')}>
              Go to Configuration →
            </button>
          )}
        </div>
      </div>
    );
  }
  
  return (
    <div className="futures-dashboard-pro">
      {/* Page Header */}
      <div className="page-header-pro">
        <div className="header-left">
          <h1 className="page-title-pro">⚡ Futures Trading Bot</h1>
          <p className="page-subtitle-pro">TradingView Signal Execution with Leverage</p>
        </div>
        <div className="header-right">
          {isAdmin && (
            <UserSelector
              currentUser={{ _id: currentUserId }}
              selectedUserId={targetUserId}
              onUserChange={setTargetUserId}
            />
          )}
          {isAdmin && (
            <button className="btn btn-secondary" onClick={() => navigate('/futures/config')}>
              ⚙️ Configure
            </button>
          )}
        </div>
      </div>

      {/* Status Bar */}
      <div className="status-bar-pro">
        <div className="status-item">
          <span className={`status-dot ${config.botEnabled ? 'running' : 'stopped'}`}></span>
          <span className="status-text">{config.botEnabled ? 'Bot Active' : 'Bot Inactive'}</span>
        </div>
        <div className="status-item">
          <span className="status-label">Leverage:</span>
          <span className="status-value">{config.defaultLeverage}x</span>
        </div>
        <div className="status-item">
          <span className="status-label">Capital/Trade:</span>
          <span className="status-value">{(config.capitalUsagePercent * 100).toFixed(0)}%</span>
        </div>
        {config.botEnabled && (
          <div className="status-item">
            <span className="webhook-indicator">📡 Listening for signals...</span>
          </div>
        )}
      </div>

      {/* Main Stats */}
      <div className="stats-row-pro">
        <StatCard
          title="Open Positions"
          value={positions.length.toString()}
          icon="📋"
        />
        <StatCard
          title="Unrealized PnL"
          value={formatCurrency(stats?.unrealizedPnl || 0)}
          trend={stats?.unrealizedPnl >= 0 ? 'up' : 'down'}
          icon="⏳"
        />
        <StatCard
          title="Realized PnL"
          value={formatCurrency(stats?.totalPnl || 0)}
          trend={stats?.totalPnl >= 0 ? 'up' : 'down'}
          icon="💰"
        />
        <StatCard
          title="Win Rate"
          value={formatPercent(stats?.winRate || 0, 0, false)}
          icon="🎯"
        />
      </div>

      {/* Open Positions */}
      <div className="card-pro card-pro-large">
        <div className="card-header-pro">
          <h3>📊 Open Positions ({positions.length})</h3>
        </div>
        <div className="card-body-pro">
          {positions.length === 0 ? (
            <div className="empty-state-inline">
              <p>No open positions</p>
              <span>Waiting for TradingView signals...</span>
            </div>
          ) : (
            <div className="positions-table-pro">
              <table className="table-pro">
                <thead>
                  <tr>
                    <th>Symbol</th>
                    <th>Side</th>
                    <th className="text-right">Entry Price</th>
                    <th className="text-right">Mark Price</th>
                    <th className="text-right">Size</th>
                    <th className="text-center">Leverage</th>
                    <th className="text-right">Unrealized PnL</th>
                  </tr>
                </thead>
                <tbody>
                  {positions.map((pos, idx) => (
                    <tr key={idx}>
                      <td className="symbol-cell">{pos.symbol}</td>
                      <td>
                        <span className={`side-badge-pro ${pos.side?.toLowerCase()}`}>
                          {pos.side}
                        </span>
                      </td>
                      <td className="text-right price-cell">{formatCurrency(pos.entryPrice)}</td>
                      <td className="text-right price-cell">{formatCurrency(pos.markPrice)}</td>
                      <td className="text-right">{pos.positionAmt?.toFixed(3)}</td>
                      <td className="text-center leverage-cell">{pos.leverage}x</td>
                      <td className={`text-right pnl-cell ${pos.unrealizedProfit >= 0 ? 'profit' : 'loss'}`}>
                        {formatCurrency(pos.unrealizedProfit)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Configuration Summary */}
      <div className="card-pro">
        <div className="card-header-pro">
          <h3>⚙️ Current Configuration</h3>
        </div>
        <div className="card-body-pro">
          <div className="config-grid-pro">
            <div className="config-item-pro">
              <span className="config-label-pro">Capital per Trade</span>
              <span className="config-value-pro">{(config.capitalUsagePercent * 100).toFixed(0)}%</span>
            </div>
            <div className="config-item-pro">
              <span className="config-label-pro">Default Leverage</span>
              <span className="config-value-pro">{config.defaultLeverage}x</span>
            </div>
            <div className="config-item-pro">
              <span className="config-label-pro">Allowed Symbols</span>
              <span className="config-value-pro">{config.allowedSymbols?.join(', ')}</span>
            </div>
            <div className="config-item-pro">
              <span className="config-label-pro">Auto Stop Loss</span>
              <span className="config-value-pro">
                {config.autoStopLossEnabled ? `${(config.autoStopLossPct * 100).toFixed(0)}%` : 'Disabled'}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
